import * as React from 'react';
import { CSSInterpolation } from '@emotion/serialize';

import { useTheme } from '../../theme';
import {
  CSSDimension,
  CSSRulesFunction,
  ResponsiveValue,
} from '../../theme/types';
import {
  AlignItems,
  AlignSelf,
  BorderScale,
  BorderStyle,
  Color,
  FlexDirection,
  FlexWrap,
  GridDisplay,
  GridPlacement,
  JustifyContent,
  Overflow,
  PlaceSelf,
  DisplayPosition,
  RadiusScale,
  SpaceScale,
  ZIndex,
} from '../../theme/tokens';
import { testResponsiveValue } from '../../theme/utils';
import { omit } from '../../utils';
import { CSSGridProperties } from './appearance';
import { withDeprecationWarnings } from '../_internal/DeprecationWarning';

export type HTMLProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  keyof AppearanceProps
>;

export interface CSSGridProps {
  gridTemplateColumns?: ResponsiveValue<string>;
  gridTemplateRows?: ResponsiveValue<string>;
  gridTemplateAreas?: ResponsiveValue<string>;
  gridArea?: ResponsiveValue<string>;
  gap?: ResponsiveValue<string | SpaceScale>;
  columnGap?: ResponsiveValue<string>;
  rowGap?: ResponsiveValue<string>;
  justifyItems?: ResponsiveValue<GridPlacement>;
  placeItems?: ResponsiveValue<GridPlacement>;
  gridColumn?: ResponsiveValue<string>;
  gridRow?: ResponsiveValue<string>;
}

interface AppearanceProps extends CSSGridProps {
  width?: ResponsiveValue<CSSDimension>;
  height?: ResponsiveValue<CSSDimension>;
  minWidth?: ResponsiveValue<CSSDimension>;
  minHeight?: ResponsiveValue<CSSDimension>;
  maxWidth?: ResponsiveValue<CSSDimension>;
  maxHeight?: ResponsiveValue<CSSDimension>;
  bg?: ResponsiveValue<Color>;
  // This should use Display enum, saving for next
  // breaking change
  display?: ResponsiveValue<
    | 'none'
    | 'block'
    | 'inline-block'
    | 'flex'
    | 'inline-flex'
    | 'inline'
    | 'inherit'
    | 'initial'
    | GridDisplay
  >;
  color?: ResponsiveValue<Color>;
  paddingV?: ResponsiveValue<SpaceScale>;
  paddingH?: ResponsiveValue<SpaceScale>;
  paddingBottom?: ResponsiveValue<SpaceScale>;
  paddingRight?: ResponsiveValue<SpaceScale>;
  flexGrow?: ResponsiveValue<number>;
  flexShrink?: ResponsiveValue<number>;
  flexBasis?: ResponsiveValue<string>;
  borderRadius?: RadiusScale;
  borderWidth?: BorderScale;
  borderTopWidth?: BorderScale;
  borderLeftWidth?: BorderScale;
  borderRightWidth?: BorderScale;
  borderBottomWidth?: BorderScale;
  borderColor?: Color;
  borderStyle?: BorderStyle;
  flexDirection?: ResponsiveValue<FlexDirection>;
  wrap?: ResponsiveValue<boolean>;
  justifyContent?: ResponsiveValue<JustifyContent | GridPlacement>;
  alignItems?: ResponsiveValue<AlignItems | GridPlacement>;
  alignSelf?: ResponsiveValue<AlignSelf>;
  placeSelf?: ResponsiveValue<PlaceSelf>;
  overflowX?: ResponsiveValue<Overflow>;
  overflowY?: ResponsiveValue<Overflow>;
  position?: ResponsiveValue<DisplayPosition>;
  top?: ResponsiveValue<CSSDimension>;
  right?: ResponsiveValue<CSSDimension>;
  bottom?: ResponsiveValue<CSSDimension>;
  left?: ResponsiveValue<CSSDimension>;
  boxShadow?: ResponsiveValue<string>;
  zIndex?: ResponsiveValue<ZIndex>;
}

export interface BoxProps extends AppearanceProps, HTMLProps {}

export const boxStyle: CSSRulesFunction<AppearanceProps> = (theme, props) => {
  const width = theme.responsiveStyle(props.width, ['width']);
  const height = theme.responsiveStyle(props.height, ['height']);
  const minWidth = theme.responsiveStyle(props.minWidth, ['minWidth']);
  const minHeight = theme.responsiveStyle(props.minHeight, ['minHeight']);
  const maxWidth = theme.responsiveStyle(props.maxWidth, ['maxWidth']);
  const maxHeight = theme.responsiveStyle(props.maxHeight, ['maxHeight']);
  const color = theme.responsiveColor(props.color, ['color']);
  const bg = theme.responsiveColor(props.bg, ['backgroundColor']);

  const paddingV = theme.responsiveSpacing(props.paddingV, [
    'paddingTop',
    'paddingBottom',
  ]);
  const paddingH = theme.responsiveSpacing(props.paddingH, [
    'paddingLeft',
    'paddingRight',
  ]);
  const paddingBottom = theme.responsiveSpacing(props.paddingBottom, [
    'paddingBottom',
  ]);
  const paddingRight = theme.responsiveSpacing(props.paddingRight, [
    'paddingRight',
  ]);
  const overflowX = theme.responsiveStyle(props.overflowX, ['overflowX']);
  const overflowY = theme.responsiveStyle(props.overflowY, ['overflowY']);

  const flexDirection = theme.responsiveStyle(props.flexDirection, [
    'flexDirection',
  ]);
  const flexWrap = theme.responsive(props.wrap || false, (value: boolean) => ({
    flexWrap: value ? FlexWrap.Wrap : FlexWrap.Nowrap,
  }));
  const flexGrow = theme.responsiveStyle(props.flexGrow, ['flexGrow']);
  const flexShrink = theme.responsiveStyle(props.flexShrink, ['flexShrink']);
  const flexBasis = theme.responsiveStyle(props.flexBasis, ['flexBasis']);

  const borderRadius = theme.radius(props.borderRadius);
  // BorderWidth needs to be before other border width styling
  const borderWidth = theme.border.width(props.borderWidth || 0);
  const borderTopWidth = theme.border.width(
    props.borderTopWidth,
    'borderTopWidth'
  );
  const borderLeftWidth = theme.border.width(
    props.borderLeftWidth,
    'borderLeftWidth'
  );
  const borderRightWidth = theme.border.width(
    props.borderRightWidth,
    'borderRightWidth'
  );
  const borderBottomWidth = theme.border.width(
    props.borderBottomWidth,
    'borderBottomWidth'
  );
  const borderStyle = theme.border.style(props.borderStyle);
  // BorderWidth styling ended
  const borderColor = theme.border.color(props.borderColor);
  const justifyContent = theme.responsiveStyle(props.justifyContent, [
    'justifyContent',
  ]);
  const alignItems = theme.responsiveStyle(props.alignItems, ['alignItems']);
  const alignSelf = theme.responsiveStyle(props.alignSelf, ['alignSelf']);
  const placeSelf = theme.responsiveStyle(props.placeSelf, ['placeSelf']);
  const display = theme.responsiveStyle(props.display || 'block', ['display']);

  const position = theme.responsiveStyle(props.position, ['position']);
  const top = theme.responsiveStyle(props.top, ['top']);
  const right = theme.responsiveStyle(props.right, ['right']);
  const bottom = theme.responsiveStyle(props.bottom, ['bottom']);
  const left = theme.responsiveStyle(props.left, ['left']);

  const zIndex = theme.responsive(props.zIndex, (zIndex: ZIndex) => ({
    zIndex: theme.zIndex(zIndex),
  }));

  // CSS Grid styles
  const gridTemplateColumns = theme.responsiveStyle(props.gridTemplateColumns, [
    'gridTemplateColumns',
  ]);
  const gridTemplateRows = theme.responsiveStyle(props.gridTemplateRows, [
    'gridTemplateRows',
  ]);
  const gridTemplateAreas = theme.responsiveStyle(props.gridTemplateAreas, [
    'gridTemplateAreas',
  ]);
  const gridArea = theme.responsiveStyle(props.gridArea, ['gridArea']);
  const columnGap = theme.responsiveStyle(props.columnGap, ['columnGap']);
  const rowGap = theme.responsiveStyle(props.rowGap, ['rowGap']);
  const gridColumn = theme.responsiveStyle(props.gridColumn, ['gridColumn']);
  const gridRow = theme.responsiveStyle(props.gridRow, ['gridRow']);

  // TODO:DEPRECATION - `string` or `ResponsiveValue<string>` for `gap`
  // TODO: Fix type narrowing in testResponsiveValue
  const gap = testResponsiveValue(val => typeof val === 'number')(props.gap)
    ? theme.responsiveSpacing(props.gap as ResponsiveValue<SpaceScale>, ['gap'])
    : theme.responsiveStyle(props.gap, ['gap']);

  const justifyItems = theme.responsiveStyle(props.justifyItems, [
    'justifyItems',
  ]);
  const placeItems = theme.responsiveStyle(props.placeItems, ['placeItems']);

  // TODO - these shadows should be token/theme based
  const boxShadow = theme.responsiveStyle(props.boxShadow, ['boxShadow']);

  return [
    display,
    width,
    height,
    minWidth,
    minHeight,
    flexDirection,
    flexWrap,
    flexGrow,
    flexShrink,
    flexBasis,
    paddingV,
    paddingH,
    paddingBottom,
    paddingRight,
    color,
    bg,
    borderRadius,
    borderWidth,
    borderTopWidth,
    borderLeftWidth,
    borderRightWidth,
    borderBottomWidth,
    borderStyle,
    borderColor,
    overflowX,
    overflowY,
    justifyContent,
    alignItems,
    alignSelf,
    placeSelf,
    position,
    top,
    right,
    bottom,
    left,
    maxWidth,
    maxHeight,
    boxShadow,
    zIndex,
    gridTemplateColumns,
    gridTemplateRows,
    gridTemplateAreas,
    gridArea,
    columnGap,
    rowGap,
    gap,
    justifyItems,
    placeItems,
    gridColumn,
    gridRow,
  ] as CSSInterpolation;
};

// eslint-disable-next-line react/display-name
const Box = React.memo(
  React.forwardRef<HTMLDivElement, BoxProps>(function Box(props, ref) {
    const { theme } = useTheme();
    const rest = omit(props, [
      'width',
      'height',
      'minWidth',
      'minHeight',
      'maxWidth',
      'maxHeight',
      'bg',
      'display',
      'color',
      'paddingV',
      'paddingH',
      'paddingBottom',
      'paddingRight',
      'flexGrow',
      'flexShrink',
      'flexBasis',
      'borderRadius',
      'borderWidth',
      'borderTopWidth',
      'borderLeftWidth',
      'borderRightWidth',
      'borderBottomWidth',
      'borderColor',
      'borderStyle',
      'flexDirection',
      'wrap',
      'justifyContent',
      'alignItems',
      'alignSelf',
      'placeSelf',
      'overflowX',
      'overflowY',
      'position',
      'top',
      'right',
      'bottom',
      'left',
      'boxShadow',
      'zIndex',
      ...CSSGridProperties,
    ]);

    return <div css={boxStyle(theme, props)} {...rest} ref={ref} />;
  })
);

Box.displayName = 'Box';

export default withDeprecationWarnings(Box, [
  {
    prop: 'gap',
    message:
      'Passing a `string` | `ResponsiveValue<string>` for gap property will be deprecated in the next major version',
    predicate: testResponsiveValue(v => typeof v === 'string'),
  },
]);

export const BoxStoryComponent = Box;
